import React, { PropsWithChildren } from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import classNames from 'classnames';
import {
  borderColor,
  BorderColorProps,
  borderRadius,
  BorderRadiusProps,
  borders,
  BordersProps,
  boxShadow,
  BoxShadowProps,
  color,
  display,
  DisplayProps,
  fontSize,
  FontSizeProps,
  fontWeight,
  FontWeightProps,
  overflow,
  OverflowProps,
  space,
  SpaceProps,
  style,
  textAlign,
  TextAlignProps,
  themeGet
} from 'styled-system';

import { Classes } from '@blueprintjs/core';
import borderColors from './utils/borderColors';
import flex from './utils/flex';
import grid from './utils/grid';
import size, { SizeProps } from './utils/size';
import position, { PositionProps } from './utils/position';

type StyledCardProps = BorderColorProps &
  BorderRadiusProps &
  BordersProps &
  BoxShadowProps &
  DisplayProps &
  FontSizeProps &
  FontWeightProps &
  OverflowProps &
  PositionProps &
  SizeProps &
  SpaceProps &
  TextAlignProps & {
    theme?: DefaultTheme;
    bg?: string;
    color?: string;
    overflowX?: string;
    overflowY?: string;
    transition?: string;
  };

const StyledCard = styled.div<StyledCardProps>`
  ${borderColor}
  ${borderRadius}
  ${borders}
  ${boxShadow}
  ${display}
  ${color}
  ${display}
  ${fontSize}
  ${fontWeight}
  ${space}
  ${overflow}
  ${position}
  ${style({
    prop: 'overflowX'
  })}
  ${style({
    prop: 'overflowY'
  })}
  ${style({
    prop: 'transition'
  })}
  ${textAlign}

  ${borderColors}
  ${flex}
  ${grid}
  ${size}
  ${({ theme, bg }) =>
    theme.name === 'dark' &&
    css`
      &.${Classes.CARD} {
        background: ${themeGet(`colors.${bg}`, theme.colors.cardBackground)};
        border: 1px solid ${theme.colors.cardBorder};
        box-shadow: none;
      }
    `}
`;

type CardProps = PropsWithChildren<
  {
    elevation?: number;
    interactive?: boolean;
    showSkeleton?: boolean;
    className?: string;
    runningText?: boolean;
    zIndex?: number;
  } & Omit<StyledCardProps, keyof React.HTMLAttributes<HTMLDivElement>>
>;

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ elevation = 0, interactive = false, showSkeleton, className, runningText, children, ...rest }, ref) => (
    <StyledCard
      ref={ref}
      className={classNames(Classes.CARD, className, {
        [`bp4-elevation-${elevation}`]: true,
        [Classes.RUNNING_TEXT]: runningText,
        [Classes.INTERACTIVE]: interactive,
        [Classes.SKELETON]: showSkeleton
      })}
      p={0}
      {...rest}
    >
      {children}
    </StyledCard>
  )
);

export default Card;
